
import { computed, defineComponent, onMounted, ref, Ref } from 'vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { useStore } from 'vuex'
import { callApiWrapper } from '@/views/callApiWrapper'
import { keieiJohoRepositories } from '@/repositories/keiei-joho/keieiJohoRepositories'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import {
  CorporationInfoPick,
  FacilityInfoPick,
  FacilityInfoPickPick,
  KeieiJokyoKmkType,
  ResponseKeieiJokyoExportLayout
} from '@/repositories/keiei-joho/keiei-jokyo/data'
import { hospitalClinicKbnType } from '@/views/corporation-info/types'
import KeieiJokyoRegisterItem from '@/views/keiei-joho/keiei-info/KeieiJokyoRegisterItem.vue'

const repository = keieiJohoRepositories.keieiJokyoRegister

export default defineComponent({
  components: {
    KeieiJokyoRegisterItem,
    ResponseResultAlert,
    ContentLayout
  },
  setup () {
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const hasError = ref<boolean>(false)
    const store = useStore()

    const isDisabled = computed(() => {
      return requestError.value !== null
    })

    const accountingStartDate = store.state.accountingStartDate
    const accountingEndDate = store.state.accountingEndDate

    // セレクトボックス用の施設情報
    const facilityPicks = ref<FacilityInfoPickPick[]>([])
    const selectedFacility = ref<FacilityInfoPickPick | null>(null)

    // 法人情報、施設情報
    const corporationInfo = ref<CorporationInfoPick | null>(null)
    const facilityInfo = ref<FacilityInfoPick | null>(null)

    // 科目情報
    const keieiJokyoItems = ref<KeieiJokyoKmkType[]>([])

    // 帳票部分の表示可否
    const isShowRegisterItem = ref<boolean>(false)

    // 初期値設定
    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const hasData = await repository.checkDataExists()
        if (!hasData.hasCorporationInfo) {
          requestError.value = '法人情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        if (!hasData.hasFacilityInfo) {
          requestError.value = '施設情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        if (!hasData.hasSettingData) {
          requestError.value = '科目設定が行われていません。経営状況設定画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }
        facilityPicks.value = await repository.getFacilityInfoPick()
      })
      loading.value = false
    })

    /**
     * レスポンス結果の非表示
     */
    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    /**
     * 施設情報セレクトボックスの変更時、選択施設に関連するデータを取得
     */
    const onFacilityChange = async () => {
      onDismissAlert()
      loading.value = true
      isShowRegisterItem.value = false

      const facility = selectedFacility.value
      if (facility !== null) {
        const kbn = facilityPicks.value.find(v => v.facilityKey === facility.facilityKey)?.hospitalClinicKbn
        if (kbn === hospitalClinicKbnType.NonReportable) {
          requestError.value = '選択施設の病院・診療所区分に「様式３（報告対象外の医療法人）」が選択されているため、本機能はご利用できません。'
          loading.value = false
          return
        }
        await callApiWrapper(requestError, async () => {
          const data = await repository.getKmkTypes(facility.facilityKey)
          keieiJokyoItems.value = data.kmkTypes
          corporationInfo.value = data.corporationInfo
          facilityInfo.value = data.facilityInfo
          isShowRegisterItem.value = true
        })
      }
      loading.value = false
    }

    /**
     * 登録ボタン
     */
    const onClickedRegister = async () => {
      onDismissAlert()
      loading.value = true

      await callApiWrapper(requestError, async () => {
        const facilityKey = selectedFacility.value?.facilityKey ?? ''
        await repository.save(facilityKey, keieiJokyoItems.value)
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 再集計ボタン
     */
    const onClickedRecalculate = async (callback: (data: ResponseKeieiJokyoExportLayout) => void) => {
      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const facilityKey = selectedFacility.value?.facilityKey ?? ''
        const data = await repository.recalculate(facilityKey)
        callback(data)
      })
      loading.value = false
    }

    return {
      loading,
      isSucceedRequest,
      requestError,
      hasError,
      isDisabled,

      facilityPicks,
      selectedFacility,
      keieiJokyoItems,
      isShowRegisterItem,

      corporationInfo,
      facilityInfo,

      accountingStartDate,
      accountingEndDate,

      onDismissAlert,
      onFacilityChange,
      onClickedRegister,
      onClickedRecalculate
    }
  }
})
